import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, TextField, IconButton,
  useMediaQuery, useTheme,
  CircularProgress, Alert, Fade, Stack, Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,

  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  LocalGasStation as GasIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Description as DocIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Apartment as ApartmentIcon,
} from '@mui/icons-material';

const palette = {
  navy: '#0f1b2d',
  primary: '#1e56a0',
  primaryLight: '#2b6cb0',
  accent: '#06b6d4',
  highlight: '#eab308',
  white: '#ffffff',
  grayLight: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray600: '#475569',
  gray800: '#1e293b',
};

/* ─── Decision Tree Configuration ─── */
const STEPS = {
  // Step 1: Existing or new customer
  start: {
    question: 'Sei già nostro cliente?',
    icon: <PersonIcon />,
    options: [
      { label: 'Sì, sono già cliente', value: 'existing', next: 'a_need', icon: <PersonIcon /> },
      { label: 'No, sono un nuovo cliente', value: 'new', next: 'b_type', icon: <PersonAddIcon /> },
    ],
  },

  // ─── PATH A: Existing Customer ───
  a_need: {
    question: 'Di cosa hai bisogno?',
    icon: <BuildIcon />,
    options: [
      { label: 'Rifornimento GPL', value: 'rifornimento', next: 'a_rifornimento', icon: <GasIcon /> },
      { label: 'Manutenzione o riparazione', value: 'manutenzione', next: 'a_manutenzione', icon: <BuildIcon /> },
      { label: 'Segnalazione problema urgente', value: 'urgente', next: 'a_urgente', icon: <WarningIcon /> },
      { label: 'Documenti e fatture', value: 'documenti', next: 'a_documenti', icon: <DocIcon /> },
    ],
  },
  a_rifornimento: {
    question: "Qual è la situazione?",
    icon: <GasIcon />,
    options: [
      { label: 'Rifornimento programmato (ho ancora GPL)', value: 'programmato', next: 'contact_existing' },
      { label: 'Serbatoio quasi vuoto', value: 'quasi_vuoto', next: 'contact_existing' },
      { label: 'Ho finito il GPL — urgente', value: 'urgente', next: 'contact_existing' },
    ],
  },
  a_manutenzione: {
    question: 'Che tipo di problema?',
    icon: <BuildIcon />,
    options: [
      { label: 'Contatore non funziona o errore lettura', value: 'contatore', next: 'contact_existing' },
      { label: 'Problema al serbatoio o tubazioni', value: 'serbatoio', next: 'contact_existing' },
      { label: 'Manutenzione ordinaria programmata', value: 'ordinaria', next: 'contact_existing' },
    ],
  },
  a_urgente: {
    question: 'Cosa sta succedendo?',
    icon: <WarningIcon />,
    options: [
      { label: 'Odore di gas', value: 'odore_gas', next: 'contact_existing' },
      { label: 'Perdita visibile', value: 'perdita', next: 'contact_existing' },
      { label: 'Guasto improvviso impianto', value: 'guasto', next: 'contact_existing' },
      { label: 'Altro problema urgente', value: 'altro', next: 'contact_existing' },
    ],
  },
  a_documenti: {
    question: 'Cosa ti serve?',
    icon: <DocIcon />,
    options: [
      { label: 'Copia fattura', value: 'fattura', next: 'contact_existing' },
      { label: 'Piano di rateizzazione', value: 'rateizzazione', next: 'contact_existing' },
      { label: 'Estratto conto', value: 'estratto_conto', next: 'contact_existing' },
      { label: 'Altro documento', value: 'altro', next: 'contact_existing' },
    ],
  },

  // ─── PATH B: New Customer ───
  b_type: {
    question: 'Che tipo di cliente sei?',
    icon: <PersonIcon />,
    options: [
      { label: 'Privato', value: 'privato', next: 'b_privato', icon: <HomeIcon /> },
      { label: 'Azienda', value: 'azienda', next: 'b_azienda_settore', icon: <BusinessIcon /> },
      { label: 'Condominio', value: 'condominio', next: 'b_condominio_unita', icon: <ApartmentIcon /> },
    ],
  },

  // Privato path
  b_privato: {
    question: 'Cosa ti interessa?',
    icon: <HomeIcon />,
    options: [
      { label: 'Installazione nuovo impianto GPL', value: 'installazione', next: 'b_abitazione' },
      { label: 'Passaggio da altro fornitore GPL', value: 'cambio_fornitore', next: 'b_abitazione' },
      { label: 'Passaggio a GPL da altro combustibile', value: 'cambio_combustibile', next: 'b_combustibile' },
      { label: 'Solo informazioni e costi', value: 'info', next: 'b_zona' },
    ],
  },
  b_abitazione: {
    question: 'Tipo di abitazione?',
    icon: <HomeIcon />,
    options: [
      { label: 'Villa / Casa indipendente', value: 'villa', next: 'b_zona' },
      { label: 'Villetta a schiera', value: 'schiera', next: 'b_zona' },
      { label: 'Casa in costruzione o ristrutturazione', value: 'costruzione', next: 'b_zona' },
    ],
  },
  b_combustibile: {
    question: 'Che combustibile usi attualmente?',
    icon: <GasIcon />,
    options: [
      { label: 'Metano', value: 'metano', next: 'b_zona' },
      { label: 'Gasolio', value: 'gasolio', next: 'b_zona' },
      { label: 'Elettrico / Pompa di calore', value: 'elettrico', next: 'b_zona' },
      { label: 'Non so / Altro', value: 'altro', next: 'b_zona' },
    ],
  },

  // Azienda path
  b_azienda_settore: {
    question: 'In che settore operi?',
    icon: <BusinessIcon />,
    options: [
      { label: 'Ristorazione / Hotel', value: 'ristorazione', next: 'b_azienda_bisogno' },
      { label: 'Agricoltura', value: 'agricoltura', next: 'b_azienda_bisogno' },
      { label: 'Industriale / Artigianale', value: 'industriale', next: 'b_azienda_bisogno' },
      { label: 'Commerciale / Uffici', value: 'commerciale', next: 'b_azienda_bisogno' },
      { label: 'Altro', value: 'altro', next: 'b_azienda_bisogno' },
    ],
  },
  b_azienda_bisogno: {
    question: 'Di cosa hai bisogno?',
    icon: <BuildIcon />,
    options: [
      { label: 'Sostituzione impianto GPL esistente', value: 'sostituzione', next: 'b_zona' },
      { label: 'Installazione nuovo impianto', value: 'installazione', next: 'b_zona' },
      { label: 'Passaggio a GPL da altro combustibile', value: 'passaggio', next: 'b_zona' },
    ],
  },

  // Condominio path
  b_condominio_unita: {
    question: 'Quante unità abitative?',
    icon: <ApartmentIcon />,
    options: [
      { label: '2–4 unità', value: '2-4', next: 'b_condominio_situazione' },
      { label: '5–10 unità', value: '5-10', next: 'b_condominio_situazione' },
      { label: '11–20 unità', value: '11-20', next: 'b_condominio_situazione' },
      { label: 'Oltre 20 unità', value: '20+', next: 'b_condominio_situazione' },
    ],
  },
  b_condominio_situazione: {
    question: 'Situazione attuale?',
    icon: <ApartmentIcon />,
    options: [
      { label: 'Nuova installazione (non c\'è impianto GPL)', value: 'nuova', next: 'b_zona' },
      { label: 'Sostituzione impianto GPL esistente', value: 'sostituzione', next: 'b_zona' },
      { label: 'Passaggio da altro combustibile a GPL', value: 'passaggio', next: 'b_zona' },
    ],
  },

  // Zona (shared for new customers)
  b_zona: {
    question: 'Zona di intervento?',
    icon: null,
    options: [
      { label: "L'Aquila", value: 'laquila', next: 'contact_new' },
      { label: 'Pescara', value: 'pescara', next: 'contact_new' },
      { label: 'Chieti', value: 'chieti', next: 'contact_new' },
      { label: 'Teramo', value: 'teramo', next: 'contact_new' },
      { label: 'Roma', value: 'roma', next: 'contact_new' },
      { label: 'Rieti', value: 'rieti', next: 'contact_new' },
      { label: 'Frosinone', value: 'frosinone', next: 'contact_new' },
      { label: 'Viterbo', value: 'viterbo', next: 'contact_new' },
      { label: 'Latina', value: 'latina', next: 'contact_new' },
    ],
  },
};

const CONTACT_PREFERENCE_OPTIONS = ['Telefono', 'Email', 'WhatsApp'];
const TIME_SLOT_OPTIONS = ['Mattina (8-12)', 'Pomeriggio (14-18)', 'Qualsiasi orario'];

/* ─── Component ─── */
const QuoteRequestDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [history, setHistory] = useState([]); // stack of { stepId, answer }
  const [currentStep, setCurrentStep] = useState('start');
  const [contact, setContact] = useState({
    nome: '', telefono: '', email: '', codiceCliente: '',
    indirizzo: '', contatto_preferito: '', fascia_oraria: '', note: '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const isExisting = history.length > 0 && history[0].answer === 'existing';
  const isContactStep = currentStep === 'contact_existing' || currentStep === 'contact_new';
  const stepConfig = STEPS[currentStep];

  // Compute stepper progress
  const getProgress = () => {
    if (sent) return { current: -1, labels: ['Completato'] };
    const total = history.length + (isContactStep ? 1 : 2);
    const current = history.length;
    return { current, total };
  };

  const reset = useCallback(() => {
    setHistory([]);
    setCurrentStep('start');
    setContact({ nome: '', telefono: '', email: '', codiceCliente: '', indirizzo: '', contatto_preferito: '', fascia_oraria: '', note: '' });
    setErrors({});
    setSending(false);
    setSent(false);
    setSendError('');
    setHoneypot('');
  }, []);

  const handleClose = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const handleOptionSelect = (option) => {
    setHistory(prev => [...prev, { stepId: currentStep, question: stepConfig.question, answer: option.value, label: option.label }]);
    setCurrentStep(option.next);
  };

  const handleBack = () => {
    if (history.length === 0) return;
    const newHistory = [...history];
    const last = newHistory.pop();
    setHistory(newHistory);
    setCurrentStep(last.stepId);
  };

  const validateContact = () => {
    const errs = {};
    if (!contact.nome.trim()) errs.nome = 'Campo obbligatorio';
    if (!contact.telefono.trim()) errs.telefono = 'Campo obbligatorio';
    if (!contact.email.trim()) errs.email = 'Campo obbligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) errs.email = 'Email non valida';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateContact()) return;
    setSending(true);
    setSendError('');

    const answers = history.map(h => ({ question: h.question, answer: h.label }));
    const contactData = { ...contact };
    // Remove empty fields
    Object.keys(contactData).forEach(k => { if (!contactData[k]) delete contactData[k]; });

    try {
      const resp = await fetch('/api/preventivo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isExistingCustomer: isExisting,
          answers,
          contact: contactData,
          honeypot,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || 'Errore nell\'invio');
      }

      setSent(true);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const progress = getProgress();

  /* ─── Render: Success Screen ─── */
  if (sent) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: 'hidden' } }}>
        <DialogContent sx={{ textAlign: 'center', py: 8, px: 4 }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%', bgcolor: '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3,
          }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: '#22c55e' }} />
          </Box>
          <Typography variant="h5" fontWeight="800" gutterBottom color={palette.gray800}>
            Richiesta Inviata!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
            Grazie {contact.nome.split(' ')[0]}! La tua richiesta è stata inviata con successo.
            <br />Ti ricontatteremo entro 24 ore.
          </Typography>
          <Button variant="contained" onClick={handleClose} sx={{
            bgcolor: palette.primary, borderRadius: 2.5, textTransform: 'none',
            fontWeight: 700, px: 4, '&:hover': { bgcolor: palette.primaryLight },
          }}>
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  /* ─── Render: Main Dialog ─── */
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: 'hidden' } }}>

      {/* Header */}
      <DialogTitle sx={{
        bgcolor: palette.navy, color: 'white', py: 2.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography variant="h6" fontWeight="700">Richiedi Preventivo</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {isContactStep
              ? 'Inserisci i tuoi dati'
              : `Domanda ${history.length + 1}`
            }
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Progress bar */}
      <Box sx={{ height: 4, bgcolor: palette.gray200 }}>
        <Box sx={{
          height: '100%', bgcolor: palette.accent, transition: 'width 0.4s ease',
          width: `${Math.min(((progress.current + 1) / (progress.total || 1)) * 100, 100)}%`,
        }} />
      </Box>

      <DialogContent sx={{ py: 4, px: 3, minHeight: isMobile ? undefined : 350 }}>
        {/* ─── Option Selection Steps ─── */}
        {!isContactStep && stepConfig && (
          <Fade in key={currentStep}>
            <Box>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: palette.gray800 }}>
                {stepConfig.question}
              </Typography>
              <Stack spacing={1.5}>
                {stepConfig.options.map((opt) => (
                  <Button
                    key={opt.value}
                    variant="outlined"
                    fullWidth
                    onClick={() => handleOptionSelect(opt)}
                    startIcon={opt.icon || null}
                    sx={{
                      justifyContent: 'flex-start',
                      py: 2, px: 3,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderColor: palette.gray200,
                      color: palette.gray800,
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: palette.accent,
                        bgcolor: `${palette.accent}08`,
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Fade>
        )}

        {/* ─── Contact Form ─── */}
        {isContactStep && (
          <Fade in>
            <Box>
              {/* Summary chips */}
              <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {history.map((h, i) => (
                  <Chip key={i} label={h.label} size="small" sx={{ bgcolor: palette.gray100, fontWeight: 500 }} />
                ))}
              </Box>

              <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: palette.gray800 }}>
                {isExisting ? 'I tuoi dati' : 'Come possiamo contattarti?'}
              </Typography>

              {sendError && <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert>}

              {/* Honeypot - hidden from users */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                tabIndex={-1}
                autoComplete="off"
              />

              <Stack spacing={2.5}>
                <TextField
                  label="Nome e Cognome"
                  required fullWidth size="small"
                  value={contact.nome}
                  onChange={e => setContact(c => ({...c, nome: e.target.value}))}
                  error={!!errors.nome} helperText={errors.nome}
                />

                {isExisting && (
                  <TextField
                    label="Codice Cliente (se lo conosci)"
                    fullWidth size="small"
                    value={contact.codiceCliente}
                    onChange={e => setContact(c => ({...c, codiceCliente: e.target.value}))}
                  />
                )}

                <TextField
                  label="Telefono"
                  required fullWidth size="small" type="tel"
                  value={contact.telefono}
                  onChange={e => setContact(c => ({...c, telefono: e.target.value}))}
                  error={!!errors.telefono} helperText={errors.telefono}
                />
                <TextField
                  label="Email"
                  required fullWidth size="small" type="email"
                  value={contact.email}
                  onChange={e => setContact(c => ({...c, email: e.target.value}))}
                  error={!!errors.email} helperText={errors.email}
                />
                <TextField
                  label={isExisting ? 'Indirizzo impianto' : 'Indirizzo (opzionale)'}
                  fullWidth size="small"
                  value={contact.indirizzo}
                  onChange={e => setContact(c => ({...c, indirizzo: e.target.value}))}
                />

                {!isExisting && (
                  <>
                    <Box>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, color: palette.gray600 }}>
                        Come preferisci essere contattato?
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {CONTACT_PREFERENCE_OPTIONS.map(opt => (
                          <Chip
                            key={opt} label={opt} clickable
                            variant={contact.contatto_preferito === opt ? 'filled' : 'outlined'}
                            color={contact.contatto_preferito === opt ? 'primary' : 'default'}
                            onClick={() => setContact(c => ({...c, contatto_preferito: opt}))}
                          />
                        ))}
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, color: palette.gray600 }}>
                        Fascia oraria preferita
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {TIME_SLOT_OPTIONS.map(opt => (
                          <Chip
                            key={opt} label={opt} clickable
                            variant={contact.fascia_oraria === opt ? 'filled' : 'outlined'}
                            color={contact.fascia_oraria === opt ? 'primary' : 'default'}
                            onClick={() => setContact(c => ({...c, fascia_oraria: opt}))}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                <TextField
                  label="Note aggiuntive"
                  fullWidth multiline rows={3} size="small"
                  value={contact.note}
                  onChange={e => setContact(c => ({...c, note: e.target.value}))}
                  placeholder="Descrivi la tua esigenza o aggiungi dettagli utili..."
                />
              </Stack>
            </Box>
          </Fade>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${palette.gray200}` }}>
        {history.length > 0 && !isContactStep && (
          <Button onClick={handleBack} startIcon={<BackIcon />}
            sx={{ textTransform: 'none', color: palette.gray600 }}>
            Indietro
          </Button>
        )}
        {isContactStep && (
          <>
            <Button onClick={handleBack} startIcon={<BackIcon />}
              sx={{ textTransform: 'none', color: palette.gray600 }}>
              Indietro
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={sending}
              endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              sx={{
                bgcolor: palette.primary, borderRadius: 2.5, textTransform: 'none',
                fontWeight: 700, px: 4, '&:hover': { bgcolor: palette.primaryLight },
              }}
            >
              {sending ? 'Invio...' : 'Invia Richiesta'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuoteRequestDialog;
